import { Link } from "react-router-dom";
import { useCompare } from "../App";
import {
  GitCompare,
  X,
  MapPin,
  ShieldCheck,
  Building2,
  Maximize,
  Calendar,
  Check,
  ArrowRight,
  Plus,
} from "lucide-react";
import { Button } from "../components/ui/button";

const formatPrice = (price) => {
  if (price >= 10000000) {
    return `₹${(price / 10000000).toFixed(2)} Cr`;
  } else if (price >= 100000) {
    return `₹${(price / 100000).toFixed(2)} L`;
  }
  return `₹${price.toLocaleString("en-IN")}`;
};

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  const getCategoryLabel = (category) => {
    switch (category) {
      case "new_launch":
        return "New Launch";
      case "under_construction":
        return "Under Construction";
      case "ready_to_move":
        return "Ready to Move";
      default:
        return category;
    }
  };

  const allAmenities = [...new Set(compareList.flatMap((p) => p.amenities || []))];

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen pt-20 bg-stone-50 flex items-center justify-center" data-testid="compare-page-empty">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <GitCompare className="w-10 h-10 text-stone-400" strokeWidth={1} />
          </div>
          <h1 className="font-display text-2xl text-stone-900 mb-3">No Properties to Compare</h1>
          <p className="text-stone-500 mb-6">
            Add properties to your compare list by clicking the compare icon on any property card.
          </p>
          <Link to="/search" className="btn-primary inline-flex items-center gap-2">
            Browse Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-stone-50" data-testid="compare-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-100">
        <div className="section-container py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl md:text-3xl text-stone-900 flex items-center gap-2">
                <GitCompare className="w-6 h-6" strokeWidth={1.5} />
                Compare Properties
              </h1>
              <p className="text-stone-500">{compareList.length} of 4 properties selected</p>
            </div>
            <Button onClick={clearCompare} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-compare-btn">
              Clear All
            </Button>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="section-container py-8">
        <div className="bg-white rounded-sm shadow-layered overflow-hidden">
          {/* Property Images & Names */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Property</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 relative">
                <button
                  onClick={() => removeFromCompare(property.property_id)}
                  className="absolute top-2 right-2 p-1 bg-stone-100 hover:bg-stone-200 rounded-full transition-colors"
                  data-testid={`remove-compare-${property.property_id}`}
                >
                  <X className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                </button>
                <Link to={`/property/${property.property_id}`}>
                  <img
                    src={property.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400"}
                    alt={property.name}
                    className="w-full aspect-[4/3] object-cover rounded-sm mb-3"
                  />
                  <h3 className="font-display text-lg text-stone-900 hover:text-emerald-900 transition-colors">
                    {property.name}
                  </h3>
                  <p className="text-sm text-stone-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" strokeWidth={1.5} />
                    {property.locality}, {property.city}
                  </p>
                </Link>
              </div>
            ))}
            {/* Add More Slots */}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-${i}`} className="p-4 flex items-center justify-center border-l border-stone-100">
                <Link
                  to="/search"
                  className="w-full aspect-[4/3] border-2 border-dashed border-stone-200 rounded-sm flex flex-col items-center justify-center text-stone-400 hover:border-emerald-900 hover:text-emerald-900 transition-colors"
                >
                  <Plus className="w-8 h-8 mb-2" strokeWidth={1} />
                  <span className="text-sm">Add Property</span>
                </Link>
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Price Range</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="font-mono text-lg text-stone-900">
                  {formatPrice(property.price_min)} - {formatPrice(property.price_max)}
                </p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-price-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Configuration */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Configuration</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="text-stone-700">{property.configurations.join(", ")}</p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-config-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Carpet Area */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Carpet Area</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="font-mono text-stone-700">
                  {property.carpet_area_min} - {property.carpet_area_max} sq.ft
                </p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-area-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Status */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Status</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <span className="status-badge">{getCategoryLabel(property.category)}</span>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-status-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Possession */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Possession</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="text-stone-700">{property.possession_date}</p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-possession-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Builder */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Builder</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="text-stone-700">{property.builder}</p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-builder-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* RERA */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">RERA ID</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <span className="rera-badge">
                  <ShieldCheck className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                  {property.rera_id.length > 20 ? property.rera_id.slice(0, 20) + "..." : property.rera_id}
                </span>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-rera-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Rating */}
          <div className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
            <div className="p-4 bg-stone-50 font-medium text-stone-700 text-sm">Rating</div>
            {compareList.map((property) => (
              <div key={property.property_id} className="p-4 border-l border-stone-100">
                <p className="font-mono text-stone-700">{property.rating}/5 ({property.reviews_count} reviews)</p>
              </div>
            ))}
            {Array.from({ length: 4 - compareList.length }).map((_, i) => (
              <div key={`empty-rating-${i}`} className="p-4 border-l border-stone-100" />
            ))}
          </div>

          {/* Amenities */}
          <div className="p-4 bg-stone-50 border-b border-stone-100">
            <h3 className="font-medium text-stone-700">Amenities Comparison</h3>
          </div>
          {allAmenities.slice(0, 10).map((amenity) => (
            <div key={amenity} className="grid grid-cols-[200px_repeat(4,1fr)] border-b border-stone-100">
              <div className="p-3 bg-stone-50 text-sm text-stone-600">{amenity}</div>
              {compareList.map((property) => (
                <div key={property.property_id} className="p-3 border-l border-stone-100 flex items-center justify-center">
                  {property.amenities?.includes(amenity) ? (
                    <Check className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                  ) : (
                    <X className="w-5 h-5 text-stone-300" strokeWidth={1.5} />
                  )}
                </div>
              ))}
              {Array.from({ length: 4 - compareList.length }).map((_, i) => (
                <div key={`empty-amenity-${i}`} className="p-3 border-l border-stone-100" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
